'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { isFirebaseClientConfigured, getVapidKey } from '../lib/firebase.client';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

interface PushTokenState {
  permission: PermissionState;
  registered: boolean;
  showOptIn: boolean;
  requestPermission: () => void;
  dismissOptIn: () => void;
}

const STORAGE_KEY = 'hms-push-optin-dismissed';
const SW_PATH = '/firebase-messaging-sw.js';

/**
 * Convert a base64url VAPID key to the Uint8Array `PushManager.subscribe`
 * requires. It rejects the string form outright, so this is not optional.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalised);

  // Backed by an explicit ArrayBuffer: `applicationServerKey` requires a
  // BufferSource over ArrayBuffer, and the default Uint8Array type is generic
  // over ArrayBufferLike (which includes SharedArrayBuffer).
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Obtain a real push subscription and hand its endpoint token to the server.
 *
 * This used to POST `fcm-demo-${Date.now()}-${random}` — a string that passed
 * the endpoint's format check and was stored as a device token. Nothing ever
 * failed loudly: registration returned 200, the row landed in
 * `push_device_tokens`, and every later send went to an address that does not
 * exist. Staff simply never got a push for an incoming call, and the only
 * symptom was silence.
 *
 * The real token is the subscription endpoint's final path segment, which is
 * what FCM's HTTP v1 API accepts as a registration token (see
 * `server/integrations/push/fcm-client.ts`).
 */
async function subscribeForPush(): Promise<string | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  if (typeof window === 'undefined' || !('PushManager' in window)) return null;

  const vapidKey = getVapidKey();
  if (!vapidKey) return null;

  const registration = await navigator.serviceWorker.register(SW_PATH);

  // Reuse an existing subscription when there is one — re-subscribing with the
  // same key returns the same endpoint, but calling it needlessly on every
  // mount is a round trip to the push service for no gain.
  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Required by Chrome: a subscription that can send silent pushes is
      // rejected outright.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    }));

  const endpoint = subscription.endpoint;
  const token = endpoint.slice(endpoint.lastIndexOf('/') + 1);
  return token || null;
}

/**
 * The browser's current notification permission, or 'unsupported'.
 *
 * Read during render via a lazy `useState` initialiser rather than synced from
 * an effect: the value is available synchronously and never changes without a
 * user action we already handle, so an effect only bought an extra render pass
 * — and on the server, where `window` is undefined, it also meant the first
 * paint claimed 'default' on a browser that had already denied.
 */
function readPermission(): PermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission as PermissionState;
}

/**
 * Whether to prompt this user to enable notifications.
 *
 * Like `readPermission`, computed during render instead of set from an effect
 * — every input (permission, localStorage, config) is synchronously readable.
 * `isAuthenticated` is passed rather than closed over so the initialiser has
 * no stale-capture hazard.
 */
function shouldPromptOptIn(isAuthenticated: boolean): boolean {
  if (!isAuthenticated) return false;
  if (readPermission() !== 'default') return false;
  if (!isFirebaseClientConfigured()) return false;
  try {
    return !localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage can throw in private mode or when cookies are blocked. Not
    // prompting is the safe default — a prompt we cannot record a dismissal
    // for would reappear on every page load.
    return false;
  }
}

export function usePushToken(isAuthenticated: boolean): PushTokenState {
  const [permission, setPermission] = useState<PermissionState>(readPermission);
  const [registered, setRegistered] = useState(false);
  const [showOptIn, setShowOptIn] = useState(() => shouldPromptOptIn(isAuthenticated));
  const initialized = useRef(false);

  const registerToken = useCallback(async () => {
    try {
      const token = await subscribeForPush();
      if (!token) {
        // No VAPID key, no service worker, or the user's browser refused. Push
        // is a fallback for when the socket is closed, so the app still works
        // — but registering a placeholder instead would make the failure
        // invisible and the device unreachable forever.
        return;
      }

      const res = await fetch('/api/v1/push/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          device: navigator.userAgent.slice(0, 100),
          appVersion: '1.0.0',
        }),
      });

      if (res.ok) {
        setRegistered(true);
      }
    } catch {
      // Silent fail — push is a best-effort fallback for a closed socket.
    }
  }, []);

  // The effect now does one thing: register with the push service when
  // permission is already granted. That is a genuine external-system sync —
  // it subscribes and POSTs — which is exactly what an effect is for. The
  // opt-in prompt is derived during render instead (see `shouldPromptOptIn`).
  useEffect(() => {
    if (initialized.current) return;
    if (!isAuthenticated) return;
    if (readPermission() !== 'granted') return;
    if (!isFirebaseClientConfigured()) return;

    initialized.current = true;

    // The rule cannot see through the async boundary: `registerToken` awaits a
    // push subscription and a fetch before it touches state, so nothing is set
    // synchronously here and no cascading render occurs. This is the
    // "subscribe to an external system, setState when it responds" shape the
    // rule's own guidance permits.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void registerToken();
  }, [isAuthenticated, registerToken]);

  const requestPermission = useCallback(async () => {
    setShowOptIn(false);

    if (!('Notification' in window)) return;

    try {
      const result = await Notification.requestPermission();
      setPermission(result as PermissionState);

      if (result === 'granted') {
        await registerToken();
      }
    } catch {
      setPermission('denied');
    }
  }, [registerToken]);

  const dismissOptIn = useCallback(() => {
    setShowOptIn(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  }, []);

  return {
    permission,
    registered,
    showOptIn,
    requestPermission,
    dismissOptIn,
  };
}
